import { Injectable } from '@angular/core';

const DARK_MODE_CLASS = 'dark-mode';
const THEME_MODE_STORAGE_KEY = 'theme-mode';
const LEGACY_DARK_MODE_KEY = 'dark-mode';
const PREFERS_DARK_QUERY = '(prefers-color-scheme: dark)';

/** Theme mode options: explicit light/dark or follow system */
export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * Service for reading/writing theme preferences to localStorage and applying theme to DOM.
 * Handles storage errors gracefully for embedded/iframe contexts.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemePreferenceService {
  /** Read stored theme mode preference from localStorage, with legacy key migration */
  getStoredPreference(): ThemeMode | null {
    try {
      const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }

      // If stored value exists but is invalid, replace with default 'light'
      if (stored !== null) {
        this.savePreference('light');
        return 'light';
      }

      // Fallback: check legacy dark-mode key and migrate
      const legacy = localStorage.getItem(LEGACY_DARK_MODE_KEY);
      if (legacy === 'true') {
        this.savePreference('dark');
        return 'dark';
      }
    } catch {
      // Storage blocked (e.g., embedded/iframe contexts)
    }
    return null;
  }

  /** Get system preference for dark mode via matchMedia */
  getSystemPreference(): boolean {
    return globalThis.matchMedia?.(PREFERS_DARK_QUERY)?.matches ?? false;
  }

  /** Listen for system preference changes */
  onSystemPreferenceChange(callback: (isDark: boolean) => void): () => void {
    const mediaQuery = globalThis.matchMedia?.(PREFERS_DARK_QUERY);
    if (!mediaQuery) {
      // No-op cleanup when matchMedia not supported
      return (): void => undefined;
    }

    const handler = (e: MediaQueryListEvent): void => callback(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }

  /**
   * Listen for storage changes from other app instances (tabs/windows).
   * Fires when the 'theme-mode' key changes in localStorage.
   * Returns a cleanup function to unsubscribe.
   */
  onStorageChange(callback: (mode: ThemeMode) => void): () => void {
    if (!globalThis.window) {
      // No-op cleanup when window not available
      return (): void => undefined;
    }

    const handler = (e: StorageEvent): void => {
      // Only react to theme-mode key changes
      if (e.key !== THEME_MODE_STORAGE_KEY) {
        return;
      }

      // Validate and apply new value
      const newValue = e.newValue;
      if (newValue === 'light' || newValue === 'dark' || newValue === 'system') {
        callback(newValue);
      }
    };

    globalThis.window.addEventListener('storage', handler);
    return () => globalThis.window?.removeEventListener('storage', handler);
  }

  /** Persist theme mode preference to localStorage */
  savePreference(mode: ThemeMode): void {
    try {
      localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    } catch {
      // Best-effort persistence: ignore storage errors
    }
  }

  /** Apply dark-mode class to document element */
  applyThemeClass(isDark: boolean): void {
    if (isDark) {
      document.documentElement.classList.add(DARK_MODE_CLASS);
    } else {
      document.documentElement.classList.remove(DARK_MODE_CLASS);
    }
  }
}
